import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { propertyService } from 'services/api';
import styles from './SubscriptionGuard.module.css';

/**
 * Protege rutas que requieren membresía.
 * - super_admin / assembly_admin → siempre pasan
 * - admin 'active' / 'trial'    → pasan normalmente
 * - admin 'grace'               → pasan en solo lectura (isReadOnly=true en contexto)
 * - admin 'blocked'             → bloqueado por ProtectedRoute antes de llegar aquí
 */
export default function SubscriptionGuard({ children }) {
  const { user, isSuperAdmin, isAssemblyAdmin, isLogisticaAdmin, subscriptionState } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [status,  setStatus]  = useState('checking');
  const [message, setMessage] = useState('');

  // Rutas de asamblea — bloqueadas para plan logística
  const assemblyRoutes = ['/admin/asambleas', '/admin/preguntas', '/admin/asistentes'];
  const isAssemblyRoute = assemblyRoutes.some(r => location.pathname.startsWith(r));

  useEffect(() => {
    // Plan logística: bloquear rutas de asamblea
    if (isLogisticaAdmin && isAssemblyRoute) {
      setMessage('Tu plan Asamblea + Logística no incluye el módulo de asambleas.');
      setStatus('blocked');
      return;
    }
    // Roles que siempre pasan
    if (!user || isSuperAdmin || isAssemblyAdmin) { setStatus('ok'); return; }

    // Grace → pasa pero en solo lectura (el banner ya lo indica en AdminLayout)
    if (subscriptionState === 'grace') { setStatus('ok'); return; }

    // Probar el endpoint para detectar SUBSCRIPTION_INACTIVE del backend —
    // se usa propiedades (no asambleas) porque este guard ahora también
    // protege rutas sin relación con asambleas (torres, unidades, propietarios).
    propertyService.getAll({ limit: 1 })
      .then(() => setStatus('ok'))
      .catch(err => {
        const code = err.response?.data?.code;
        if (code === 'SUBSCRIPTION_INACTIVE') {
          setMessage(err.response.data.message || 'Membresía inactiva');
          setStatus('blocked');
        } else {
          setStatus('ok');
        }
      });
  }, [user, isSuperAdmin, isAssemblyAdmin, isLogisticaAdmin, isAssemblyRoute, subscriptionState]); // eslint-disable-line

  if (status === 'checking') {
    return (
      <div className="loading">
        <div className="spinner" />
        <p className={styles.verificando}>Verificando membresía...</p>
      </div>
    );
  }

  if (status === 'blocked') {
    return (
      <div className={styles.centro}>
        <div className={styles.tarjeta}>
          <div className={styles.candado}>
            <i className={`fas fa-lock ${styles.candadoGlifo}`} />
          </div>
          <h2 className={styles.titulo}>
            Membresía inactiva
          </h2>
          <p className={styles.mensaje}>
            {message}
          </p>
          <div className={styles.queHacer}>
            <div className={styles.queHacerTitulo}>
              <i className={`fas fa-info-circle ${styles.iconoInfo}`} />
              <span className={styles.queHacerTexto}>¿Qué hacer?</span>
            </div>
            <ul className={styles.pasos}>
              <li>Contacta al administrador de EVENTIFY</li>
              <li>Solicita la renovación o activación de tu membresía</li>
              <li>Una vez activada, podrás acceder nuevamente</li>
            </ul>
          </div>
          <button className={`btn btn-secondary ${styles.botonVolver}`} onClick={() => navigate('/dashboard')}>
            <i className={`fas fa-arrow-left ${styles.iconoVolver}`} />Volver al panel
          </button>
        </div>
      </div>
    );
  }

  return children;
}